import React from "react";
import {
    alpha,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import DeleteForeverRounded from "@mui/icons-material/DeleteForeverRounded";
import Button from "../Button";
import { FONTS } from "../../constants/styles";

interface DeleteDialogProps {
    open: boolean;
    isDeleting: boolean;
    entityName: string;
    onClose: () => void;
    onDelete: (key?: string) => void;
}

const DeleteDialog: React.FC<DeleteDialogProps> = ({
    open,
    isDeleting,
    entityName,
    onClose,
    onDelete,
}) => {
    const theme = useTheme();
    const errorColor = theme.palette.error.main;

    return (
        <Dialog
            open={open}
            onClose={!isDeleting ? onClose : undefined}
            PaperProps={{
                sx: {
                    borderRadius: "20px",
                    p: 3,
                    textAlign: "center",
                    boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
                    maxWidth: 360,
                    mx: 2,
                },
            }}
            className="force-dynamic-translation"
        >
            <DialogTitle sx={{ p: 0, pb: 0.5 }}>
                <Stack spacing={2} alignItems="center">
                    {/* Tinted icon box — error variant */}
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: "14px",
                            bgcolor: alpha(errorColor, 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                            color: errorColor,
                        }}
                    >
                        <DeleteForeverRounded sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography
                        fontFamily={FONTS.display}
                        fontWeight={700}
                        fontSize="20px"
                        color="error"
                    >
                        Delete{" "}
                        {entityName.charAt(0).toUpperCase() +
                            entityName.slice(1)}
                        ?
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 0, pt: 1 }}>
                <Typography
                    fontWeight={400}
                    fontSize="14px"
                    lineHeight="150%"
                    color="text.secondary"
                >
                    Are you sure you want to delete this {entityName}?
                    <br />
                    <strong>This action is irreversible.</strong>
                </Typography>
            </DialogContent>
            <DialogActions
                sx={{ justifyContent: "center", gap: 1, p: 0, pt: 2.5 }}
            >
                <Button
                    onClick={onClose}
                    disabled={isDeleting}
                    variant="medium"
                    color="secondary"
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => onDelete()}
                    disabled={isDeleting}
                    variant="medium"
                    customColor={errorColor}
                    loading={isDeleting}
                >
                    {isDeleting ? "Deleting..." : "Delete"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteDialog;
